import { useEffect, useRef } from 'react';
import { getAllDrafts, deleteDraft } from '../utils/draft-storage';

/**
 * On mount, checks IndexedDB for orphaned answer drafts (from crashed/interrupted
 * submissions) and silently auto-retries them. Drafts are cleared only after the
 * server confirms success.
 */
export function useDraftRecovery(
  submitAnswer: (
    conversationId: string,
    content: string,
    files?: File[],
    audio?: File,
    audioDuration?: number,
  ) => Promise<void>,
) {
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    (async () => {
      const drafts = await getAllDrafts();
      if (drafts.length === 0) return;

      for (const draft of drafts) {
        // Skip drafts older than 24 hours
        if (Date.now() - draft.timestamp > 24 * 60 * 60 * 1000) {
          await deleteDraft(draft.conversationId);
          continue;
        }

        try {
          const audioFile = draft.audioBlob
            ? new File([draft.audioBlob], `recovery-${Date.now()}.webm`, { type: 'audio/webm' })
            : undefined;

          await submitAnswer(
            draft.conversationId,
            draft.content,
            undefined,
            audioFile,
            draft.audioDuration,
          );
          await deleteDraft(draft.conversationId);
          console.info('[draft-recovery] Successfully recovered draft for', draft.conversationId);
        } catch (e) {
          console.warn('[draft-recovery] Failed to recover draft for', draft.conversationId, e);
          // Leave draft in place for next attempt
        }
      }
    })();
  }, [submitAnswer]);
}
